// src/features/webhooks/WebhookEndpointFormDialog.tsx
// "+ New" webhook endpoint — desktop-app create dialog built on the form-kit
// (FormDialog/TitleInput/AutoGrowTextarea/FormRow) from src/components/form.
// Replaces the routed /webhooks/new page. See docs/ux/14-forms.md.

import { useEffect, useState } from 'react';
import { useWebhook, useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import type { WebhookAuthType } from '@core/types/webhook';
import { EMPTY_CREATE_FORM, validateCreateForm, needsAuthConfig } from './logic';
import type { CreateEndpointFormState } from './logic';
import { EventTypeChips } from './EventTypeChips';

export interface WebhookEndpointFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create with the one-time signing secret and the new endpoint id.
   *  The caller (list page) is responsible for surfacing the secret — this dialog never renders it. */
  onCreated: (secret: string | null, id: string | null) => void;
}

const AUTH_TYPE_OPTIONS = [
  { value: 'WEBHOOK_AUTH_TYPE_HMAC', label: 'HMAC SHA-256' },
  { value: 'WEBHOOK_AUTH_TYPE_BEARER', label: 'Bearer Token' },
  { value: 'WEBHOOK_AUTH_TYPE_BASIC', label: 'Basic Auth' },
  { value: 'WEBHOOK_AUTH_TYPE_HEADER', label: 'Custom Header' },
  { value: 'WEBHOOK_AUTH_TYPE_NONE', label: 'None' },
];

export function WebhookEndpointFormDialog({ open, onClose, onCreated }: WebhookEndpointFormDialogProps) {
  const { t } = useI18n();
  const store = useWebhook();

  const [form, setForm] = useState<CreateEndpointFormState>({ ...EMPTY_CREATE_FORM });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [showMore, setShowMore] = useState(false);
  const [availableEventTypes, setAvailableEventTypes] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    store
      .ListEventTypes()
      .then((resp) => {
        const types = resp.event_types?.items?.map((et: { type: string }) => et.type) ?? [];
        setAvailableEventTypes(types);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setForm({ ...EMPTY_CREATE_FORM });
    setErrors({});
    setSubmitError(null);
    setShowMore(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleChange = (field: keyof CreateEndpointFormState, value: string | boolean | string[]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const toggleEventType = (eventType: string) => {
    setForm((prev) => {
      const current = prev.eventTypes;
      const next = current.includes(eventType)
        ? current.filter((et) => et !== eventType)
        : [...current, eventType];
      return { ...prev, eventTypes: next };
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.eventTypes;
      return next;
    });
  };

  const dirty = !!(form.name.trim() || form.url.trim() || form.description.trim());
  const canSubmit =
    !isSubmitting &&
    !!form.name.trim() &&
    !!form.url.trim() &&
    Object.keys(validateCreateForm(form)).length === 0;

  const submit = async () => {
    const validationErrors = validateCreateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await store.CreateEndpoint({
        endpoint: {
          name: form.name.trim(),
          description: form.description.trim(),
          url: form.url.trim(),
          auth_type: form.authType as WebhookAuthType,
          auth_header: form.authHeader,
          auth_value: form.authValue,
          subscribe_all: form.subscribeAll,
          event_types: form.subscribeAll ? [] : form.eventTypes,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
      const ep = response.endpoint;
      const secret = ep?.secret ?? null;
      const id = ep?.entity?.id ?? null;
      close();
      onCreated(secret, id);
    } catch (e) {
      setSubmitError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('webhooks.createEndpoint')}
      canSubmit={canSubmit}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('webhooks.createEndpoint')}
      footerLeft={
        !showMore ? (
          <button
            type="button"
            onClick={() => setShowMore(true)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {t('webhooks.moreFields')}
          </button>
        ) : undefined
      }
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError != null && (
          <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />
        )}

        <TitleInput
          value={form.name}
          onChange={(v) => handleChange('name', v)}
          placeholder={t('webhooks.fieldNamePlaceholder')}
          initialFocus
          onEnter={() => document.getElementById('webhook-create-description')?.focus()}
          aria-label={t('webhooks.fieldName')}
        />
        {errors.name && <p className="text-xs text-destructive">{t(errors.name)}</p>}

        <AutoGrowTextarea
          id="webhook-create-description"
          value={form.description}
          onChange={(v) => handleChange('description', v)}
          placeholder={t('webhooks.fieldDescriptionPlaceholder')}
          minRows={1}
        />

        <input
          type="text"
          value={form.url}
          onChange={(e) => handleChange('url', e.target.value)}
          placeholder="https://example.com/webhooks"
          aria-label={t('webhooks.fieldUrl')}
          className="w-full rounded-md border border-dashed border-input bg-transparent px-2 py-1 font-mono text-sm text-foreground placeholder:font-sans"
        />
        {errors.url && <p className="text-xs text-destructive">{t(errors.url)}</p>}

        {showMore && (
          <div className="space-y-3 border-t border-border pt-3">
            <FormRow label={t('webhooks.fieldAuthType')} labelWidth="sm">
              <Select
                value={form.authType}
                onChange={(e: { target: { value: string } }) => handleChange('authType', e.target.value)}
                options={AUTH_TYPE_OPTIONS}
                fullWidth={false}
                className="w-48"
                aria-label={t('webhooks.fieldAuthType')}
              />
            </FormRow>

            {needsAuthConfig(form.authType) && form.authType === 'WEBHOOK_AUTH_TYPE_HEADER' && (
              <FormRow label={t('webhooks.fieldAuthHeader')} labelWidth="sm">
                <input
                  type="text"
                  value={form.authHeader}
                  onChange={(e) => handleChange('authHeader', e.target.value)}
                  placeholder="X-Webhook-Token"
                  className="w-full rounded-md border border-input bg-transparent px-2 py-1 text-sm text-foreground"
                />
              </FormRow>
            )}
            {needsAuthConfig(form.authType) && form.authType !== 'WEBHOOK_AUTH_TYPE_HMAC' && (
              <FormRow label={t('webhooks.fieldAuthValue')} labelWidth="sm">
                <input
                  type="text"
                  value={form.authValue}
                  onChange={(e) => handleChange('authValue', e.target.value)}
                  placeholder={
                    form.authType === 'WEBHOOK_AUTH_TYPE_BASIC'
                      ? 'username:password'
                      : form.authType === 'WEBHOOK_AUTH_TYPE_BEARER'
                        ? 'your-token'
                        : 'your-secret-value'
                  }
                  className="w-full rounded-md border border-input bg-transparent px-2 py-1 text-sm text-foreground"
                />
              </FormRow>
            )}
            {form.authType === 'WEBHOOK_AUTH_TYPE_HMAC' && (
              <p className="text-xs text-muted-foreground">{t('webhooks.hmacHint')}</p>
            )}

            <FormRow label={t('webhooks.fieldEvents')} labelWidth="sm">
              <EventTypeChips
                availableTypes={availableEventTypes}
                selected={form.eventTypes}
                subscribeAll={form.subscribeAll}
                onToggle={toggleEventType}
                onSubscribeAllChange={(v) => handleChange('subscribeAll', v)}
              />
              {errors.eventTypes && <p className="mt-1 text-xs text-destructive">{t(errors.eventTypes)}</p>}
            </FormRow>
          </div>
        )}
      </div>
    </FormDialog>
  );
}

export default WebhookEndpointFormDialog;
